import React, { FC } from 'react';
import styled from 'styled-components';

import { BookTypeNameEnum } from '~/api/book/bookApiTypes';
import {
  AuthorLink, BookLink, GenreLink, ReaderLink,
} from '~/atomic/atom/links/dynamicLinks';
import { BookCycle } from '~/atomic/page/book/Book/BookCycle';
import { BookModerationIcon } from '~/atomic/page/book/Book/bookModeration/BookModerationIcon';
import { BookPageCover } from '~/atomic/page/book/Book/BookPageCover';
import { SCBookReadButton, SCBookReadButtonLink } from '~/atomic/page/book/Book/BookPlayerButtons';
import { PageTitleStyle, TextRegStyle } from '~/atomic/Typography';
import { defaultCycleName } from '~/feature/cycle/cycleSlice';
import { GraphqlBook } from '~/graphql/books/factory/book/BookFactoryTypes';
import { lessThan, moreThan } from '~/lib/mediaQuery';

export interface WinnerBookProps {
  winnerBook: GraphqlBook;
}

export const ContestWinnerBook: FC<WinnerBookProps> = ({ winnerBook }) => {
  const isDefaultCycle = (winnerBook?.cycle?.name ?? '') === defaultCycleName;
  const bookAuthor = winnerBook?.author;

  return (
    <SCWinnerBook>
      <SCImageWrapper>
        <BookPageCover
          book={{
            author: bookAuthor?.name,
            title: winnerBook?.name,
            genre: winnerBook?.genres?.[0],
          }}
          coverImages={winnerBook?.coverImages}
        />
      </SCImageWrapper>
      <SCBookInfo>
        <BookLink slug={winnerBook?.slug}>
          <SCBookName>
            {winnerBook?.name}
          </SCBookName>
        </BookLink>
        <AuthorLink username={bookAuthor?.username}>
          <SCBookAuthor>
            {bookAuthor?.name}
          </SCBookAuthor>
        </AuthorLink>
        <SCDescription>
          {winnerBook?.annotation}
        </SCDescription>
        <SCBookInfoGroup>
          {isDefaultCycle ? (
            <SCGenres>
              <SCDefaultBookCycle>
                {BookTypeNameEnum[winnerBook?.type] ?? ''}
              </SCDefaultBookCycle>
              {winnerBook?.genres?.map((genre, i) => (
                <GenreLink
                  slug={genre.slug}
                  key={genre.slug}
                >
                  <SCGrayGenresLink>
                    <span itemProp="genre">
                      {genre.name}
                    </span>
                  </SCGrayGenresLink>
                </GenreLink>
              )) ?? null}
              <BookModerationIcon />
            </SCGenres>
          ) : (
            <>
              <BookCycle book={winnerBook} />
              <SCGenres>
                {winnerBook?.genres?.map((genre) => (
                  <GenreLink
                    slug={genre.slug}
                    key={genre.slug}
                  >
                    <SCGenresLink>
                      {genre.name}
                    </SCGenresLink>
                  </GenreLink>
                )) ?? null}
                <BookModerationIcon />
              </SCGenres>
            </>
          )}
        </SCBookInfoGroup>
        <SCWinnerButtonWrapper>
          <ReaderLink bookSlug={winnerBook?.slug}>
            <SCBookReadButtonLink>
              <SCBookReadButton size="large" type="primary">
                Читать
              </SCBookReadButton>
            </SCBookReadButtonLink>
          </ReaderLink>
        </SCWinnerButtonWrapper>
      </SCBookInfo>
    </SCWinnerBook>
  );
};

const SCWinnerBook = styled.div`
  display: flex;
  margin-left: auto;
  margin-right: auto;
  
  ${moreThan('sm')} {
    max-width: 800px;

    > *:not(:last-child) {
      margin-right: 30px;
    }
  }
  
  ${lessThan('sm')} {
    flex-direction: column;
    align-items: center;
    max-width: 400px;
    text-align: center;

    > *:not(:last-child) {
      margin-bottom: 20px;
    }
  }
`;

const SCBookInfo = styled.div`
  display: flex;
  flex-direction: column;
  
  > *:not(:last-child) {
    margin-bottom: 12px;
  }
`;

const SCBookName = styled.a`
  ${PageTitleStyle};
`;

const SCBookAuthor = styled.a`
  ${TextRegStyle};
  color: var(--gray-color);
`;

const SCImageWrapper = styled.div`
  max-width: 250px;
  width: 100%;
  flex-shrink: 0;
`;

const SCDescription = styled.p`
  ${TextRegStyle};
  color: var(--black-color);
`;

const SCBookInfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: var(--gray4-color);
  padding: 16px;
`;

const SCGenres = styled.div`
  width: 100%;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const SCDefaultBookCycle = styled.span`
  ${TextRegStyle};
  color: var(--black-color);
  padding-right: 4px;
`;

const SCGenresLink = styled.a`
  ${TextRegStyle};
  color: var(--gray-color);
  transition: color 0.3s ease;
  margin-right: 3px;
  
  &:not(:last-of-type) {
    &:after {
      content: ",";
    }
  }

  &:hover {
    color: var(--primary-color);
  }
`;

const SCGrayGenresLink = styled(SCGenresLink)`
  color: var(--gray-color);

  &:hover {
    color: var(--primary-color);
  }
`;

const SCWinnerButtonWrapper = styled.div`
  max-width: 200px;
  width: 100%;
  
  ${lessThan('sm')} {
    margin-left: auto;
    margin-right: auto;
  }
`;
